import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import Cookies from "js-cookie";

import {
  AuthService,
  type LoginResponse,
  type MeResponse,
} from "@/api";
import { COOKIE_OPTIONS } from "@/api/client";
import { COOKIE_KEYS } from "@/lib/constants";

export interface AuthUser extends MeResponse {}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string,
    workspace_id?: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  switchWorkspace: (workspace_id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Auth provider. Boots by reading the persisted access-token cookie
 * and calling ``GET /v1/auth/me`` to hydrate the user. If the cookie
 * is missing or stale, the provider falls back to unauthenticated
 * state — the protected-route HOC will redirect to ``/login``.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const hydrate = useCallback(async () => {
    const token = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }
    try {
      const me = await AuthService.me();
      setUser(me);
    } catch {
      Cookies.remove(COOKIE_KEYS.ACCESS_TOKEN);
      Cookies.remove(COOKIE_KEYS.REFRESH_TOKEN);
      Cookies.remove(COOKIE_KEYS.WORKSPACE_ID);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  const persistTokens = useCallback((tokens: LoginResponse) => {
    Cookies.set(
      COOKIE_KEYS.ACCESS_TOKEN,
      tokens.access_token,
      COOKIE_OPTIONS
    );
    Cookies.set(
      COOKIE_KEYS.REFRESH_TOKEN,
      tokens.refresh_token,
      COOKIE_OPTIONS
    );
  }, []);

  const login = useCallback(
    async (email: string, password: string, workspace_id?: string) => {
      const tokens = await AuthService.login({
        email,
        password,
        workspace_id,
      });
      persistTokens(tokens);
      const me = await AuthService.me();
      setUser(me);
      if (me.workspace_id) {
        Cookies.set(
          COOKIE_KEYS.WORKSPACE_ID,
          me.workspace_id,
          COOKIE_OPTIONS
        );
      }
    },
    [persistTokens]
  );

  const logout = useCallback(async () => {
    try {
      await AuthService.logout();
    } catch {
      // best-effort — clear cookies regardless
    }
    Cookies.remove(COOKIE_KEYS.ACCESS_TOKEN);
    Cookies.remove(COOKIE_KEYS.REFRESH_TOKEN);
    Cookies.remove(COOKIE_KEYS.WORKSPACE_ID);
    setUser(null);
  }, []);

  const switchWorkspace = useCallback(
    async (workspace_id: string) => {
      const tokens = await AuthService.switchWorkspace({ workspace_id });
      persistTokens(tokens);
      Cookies.set(COOKIE_KEYS.WORKSPACE_ID, workspace_id, COOKIE_OPTIONS);
      const me = await AuthService.me();
      setUser(me);
    },
    [persistTokens]
  );

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await hydrate();
  }, [hydrate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        switchWorkspace,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider.");
  }
  return ctx;
}
